import EntityType from '@enums/EntityType';
import useAuthStore from '@pkg/auth/useStore';
import useLibrary from '@pkg/entities/library/useLibrary';
import usePeople from '@pkg/entities/people/mutations/useUpdate';
import useTags from '@pkg/entities/tags/useTags';

const getLibraryType = (entityType) => {
  switch (entityType) {
    case EntityType.ACTIVITY_TYPE:
      return 'activities';
    case EntityType.ROLE_TYPE:
      return 'roles';
    case EntityType.PROPERTY:
    case EntityType.ACTIVITY_PROPERTY:
    case EntityType.ACTIVITY_PROPERTY_VALUE:
    case EntityType.ROLE_PROPERTY:
    case EntityType.ROLE_PROPERTY_VALUE:
    case EntityType.GROUP_PROPERTY:
    case EntityType.GROUP_PROPERTY_VALUE:
      return 'properties';
    case EntityType.SKILL:
    case EntityType.PERSON_SKILL:
    case EntityType.ROLE_SKILL:
    case EntityType.ROLE_SKILL_LEVEL:
      return 'skills';
    case EntityType.PERSON:
    case EntityType.PERSON_SKILL_LEVEL:
      return 'people';
    case EntityType.TAG:
    case EntityType.ACTIVITY_TAG:
    case EntityType.ACTIVITY_TAG_CATEGORY:
    case EntityType.ROLE_TAG:
    case EntityType.ROLE_TAG_CATEGORY:
    case EntityType.GROUP_TAG:
    case EntityType.GROUP_TAG_CATEGORY:
      return 'tags';
    default:
  }
};

const determineLibraryAction = ({
  action,
  library,
  libraryType,
  peopleLibrary,
  tagLibrary,
}) => {
  switch (libraryType) {
    case 'people':
      return peopleLibrary;
    case 'tags':
      return tagLibrary.onDispatch;
    default:
      return library[libraryType]?.mutations[action];
  }
};

const useLibraryActions = () => {
  const library = useLibrary();
  const tagLibrary = useTags();
  const peopleLibrary = usePeople();
  const { organisation } = useAuthStore();

  const handleUpdate = async ({ action, entity, entityType, properties }) => {
    const libraryType = getLibraryType(entityType);

    const libraryAction = determineLibraryAction({
      action,
      library,
      libraryType,
      peopleLibrary,
      tagLibrary,
    });

    if (!libraryAction) {
      throw new Error();
    }

    if (entityType === EntityType.ACTIVITY_TYPE) {
      const description =
        action === 'create'
          ? properties.description
          : { description: properties.description };

      const result = await libraryAction(properties.uuid, description);
      return result;
    }

    if (entityType === EntityType.PERSON) {
      const { uuid, skills } = properties;

      const result = await libraryAction(uuid, { skills });
      return result;
    }

    if (action === 'update' && libraryType === 'properties') {
      const { name, options } = properties;
      const result = await libraryAction(entity, { name, options });
      return result;
    }

    if (action === 'create' && libraryType === 'properties') {
      const { name } = properties;
      const { uuid, options, scope, type } = entity;
      const result = await libraryAction(uuid, name, type, scope, options);
      return result;
    }

    if (action === 'remove' && libraryType === 'properties') {
      const { uuid } = properties;
      const result = await libraryAction(uuid);
      return result;
    }

    if (libraryType === 'tags') {
      const result = await libraryAction(action, properties);
      return result;
    }

    if (action === 'update' && libraryType === 'skills') {
      const { uuid, name } = properties;
      const result = await libraryAction(uuid, { name });
      return result;
    }

    if (action === 'create' && libraryType === 'skills') {
      const { uuid, name } = properties;
      const result = await libraryAction(organisation.uuid, properties);
      return result;
    }
    if (action === 'remove' && libraryType === 'skills') {
      const { uuid } = properties;
      const result = await libraryAction(uuid);
      return result;
    }

    return await libraryAction(properties);
  };

  return {
    onLibraryUpdate: handleUpdate,
  };
};

export default useLibraryActions;
